import {useEffect, useCallback, useRef} from 'react';

import {SidePanelId} from '~shared/models';

import {isBoolean} from '~ui/utils';
import {useAppDispatch, useStateSelector} from '~ui/hooks';
import {updateUserSettings} from '~ui/store/slices/user-settings';

import {usePanel} from './use-panel';

type HideByType = Partial<Record<SidePanelId, boolean>>;

export const useSidePanel = (defaultSidePanelId?: SidePanelId, hideByType?: HideByType) => {
    const {sidePanel} = useStateSelector((state) => state.userSettings);

    const {panelVisible, activePanel, switchPanel, hidePanel, showPanel} =
        usePanel<SidePanelId>(defaultSidePanelId);

    const settingsActiveSidePanel = sidePanel?.active;
    const settingsSidePanelVisible = sidePanel?.visible;

    const dispatch = useAppDispatch();

    const isMountedRef = useRef(false);

    const shouldHideActivePanel = settingsActiveSidePanel
        ? hideByType?.[settingsActiveSidePanel]
        : false;

    useEffect(() => {
        if (shouldHideActivePanel) {
            hidePanel();
            isMountedRef.current = true;
            return;
        }

        if (!isBoolean(settingsSidePanelVisible)) {
            isMountedRef.current = true;
            return;
        }

        if (settingsSidePanelVisible) {
            if (isMountedRef.current || settingsActiveSidePanel !== 'revisions') {
                showPanel(settingsActiveSidePanel);
            }
        } else {
            hidePanel();
        }
        isMountedRef.current = true;
    }, [
        showPanel,
        hidePanel,
        settingsActiveSidePanel,
        settingsSidePanelVisible,
        isMountedRef,
        shouldHideActivePanel,
    ]);

    const showSidePanel = useCallback(
        (id?: SidePanelId) => {
            showPanel(id);
            dispatch(
                updateUserSettings({
                    sidePanel: {
                        visible: true,
                        active: id,
                    },
                }),
            );
        },
        [dispatch, showPanel],
    );

    const hideSidePanel = useCallback(() => {
        hidePanel();
        dispatch(
            updateUserSettings({
                sidePanel: {
                    visible: false,
                },
            }),
        );
    }, [dispatch, hidePanel]);

    const switchSidePanel = useCallback(
        (id: SidePanelId) => {
            switchPanel(id, (visible) => {
                dispatch(
                    updateUserSettings({
                        sidePanel: {
                            visible,
                            active: id,
                        },
                    }),
                );
            });
        },
        [dispatch, switchPanel],
    );

    return {
        sidePanelVisible: panelVisible,
        activeSidePanel: activePanel,
        showSidePanel,
        hideSidePanel,
        switchSidePanel,
    };
};
